import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Calendar, User, ArrowLeft, Tag, Clock, Share2 } from 'lucide-react';
import { useScrollAnimations } from '../hooks/useScrollAnimations';
import { Helmet } from 'react-helmet-async';
import api, { API_BASE } from '../lib/api';
import { applySeo, canonicalFor } from '../lib/seo';

// Share Button Component
const ShareButtons = ({ url, title, summary, imageUrl, slug }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const buttonRef = React.useRef(null);
  
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);
  const encodedSummary = encodeURIComponent(summary || '');
  
  // For Facebook/LinkedIn, use the OG endpoint that serves proper meta tags
  // This endpoint serves HTML with OG tags and redirects users to the actual blog post
  const ogUrl = slug ? `${API_BASE}/og/blog/${slug}` : url;
  const encodedOgUrl = encodeURIComponent(ogUrl);
  
  // Check if Web Share API is available (for native mobile sharing)
  const canUseNativeShare = navigator.share !== undefined;

  // Native share using Web Share API - opens native share sheet on mobile
  const handleNativeShare = async () => {
    try {
      await navigator.share({
        title: title,
        text: summary || title,
        url: url
      });
      setShowDropdown(false);
    } catch (err) {
      console.log('Share cancelled or failed:', err);
    }
  };

  // Share URLs
  const shareLinks = {
    // Facebook uses OG endpoint for proper preview (title, image, description)
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedOgUrl}`,
    // LinkedIn also uses OG endpoint
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedOgUrl}`,
    // Twitter and WhatsApp use direct URL
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    whatsapp: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
    email: `mailto:?subject=${encodedTitle}&body=${encodedSummary}%0A%0ARead more: ${encodedUrl}`
  };

  const handleToggle = () => {
    if (!showDropdown && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = 320; // approximate height of dropdown
      
      // If not enough space below, open upwards
      if (spaceBelow < dropdownHeight) {
        setDropdownPosition({
          top: rect.top - dropdownHeight - 8,
          left: rect.left
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left
        });
      }
    }
    setShowDropdown(!showDropdown);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      alert('Link copied to clipboard!');
      setShowDropdown(false);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  return (
    <div style={{ position: 'relative', display: 'inline-block' }}>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: '#666',
          background: 'none',
          border: '1px solid #ddd',
          padding: '8px 14px',
          borderRadius: '6px',
          cursor: 'pointer',
          fontSize: '0.9rem',
          fontWeight: '500',
          transition: 'all 0.2s'
        }}
        onMouseEnter={(e) => {
          e.target.style.borderColor = 'var(--royal-gold)';
          e.target.style.color = 'var(--royal-gold)';
        }}
        onMouseLeave={(e) => {
          e.target.style.borderColor = '#ddd';
          e.target.style.color = '#666';
        }}
      >
        <Share2 size={16} />
        Share
      </button>
      
      {showDropdown && (
        <div style={{
          position: 'fixed',
          top: dropdownPosition.top,
          left: dropdownPosition.left,
          background: 'white',
          borderRadius: '8px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          padding: '8px 0',
          minWidth: '180px',
          zIndex: 10000
        }}>
          {/* Native Share option - appears first on mobile */}
          {canUseNativeShare && (
            <>
              <button
                onClick={handleNativeShare}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '10px 16px',
                  color: '#333',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  fontSize: '0.9rem',
                  cursor: 'pointer',
                  width: '100%',
                  textAlign: 'left',
                  borderRadius: '4px',
                  margin: '0 8px 8px',
                  maxWidth: 'calc(100% - 16px)'
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
                <span style={{ color: 'white', fontWeight: '500' }}>Share via Apps...</span>
              </button>
              <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
            </>
          )}
          
          <a
            href={shareLinks.facebook}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => setShowDropdown(false)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            Facebook
          </a>
          <a
            href={shareLinks.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
            WhatsApp
          </a>
          <a
            href={shareLinks.twitter}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#000"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            X / Twitter
          </a>
          <a
            href={shareLinks.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
            LinkedIn
          </a>
          <a
            href={shareLinks.email}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              textDecoration: 'none',
              fontSize: '0.9rem'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#666"><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/></svg>
            Email
          </a>
          <div style={{ borderTop: '1px solid #eee', margin: '4px 0' }} />
          <button
            onClick={copyToClipboard}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              padding: '10px 16px',
              color: '#333',
              background: 'none',
              border: 'none',
              fontSize: '0.9rem',
              cursor: 'pointer',
              width: '100%',
              textAlign: 'left'
            }}
            onMouseEnter={(e) => e.target.style.background = '#f5f5f5'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="#666"><path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z"/></svg>
            Copy Link
          </button>
        </div>
      )}
      
      {/* Click outside to close */}
      {showDropdown && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 99
          }}
          onClick={() => setShowDropdown(false)}
        />
      )}
    </div>
  );
};

// Blog List Page
export const Blog = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorCode, setErrorCode] = useState(null);
  const [retryCount, setRetryCount] = useState(0);

  useEffect(() => {
    // The build-time snapshot has no API; keep the loading state so the HTML matches hydration.
    if (navigator.userAgent === 'ReactSnap') return;
    const fetchPosts = async () => {
      setLoading(true);
      setError(null);
      setErrorCode(null);
      
      const apiEndpoint = '/blog/posts';
      console.log('Fetching blog posts from:', `${API_BASE}${apiEndpoint}`);
      
      try {
        const response = await api.get(apiEndpoint, {
          timeout: 15000 // 15 second timeout
        });
        setPosts(response.data.posts || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching blog posts:', err);
        
        // Determine error type and code
        let code = 'UNKNOWN';
        let message = 'An unexpected error occurred';
        
        if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
          code = 'TIMEOUT';
          message = 'Request timed out. The server took too long to respond.';
        } else if (err.code === 'ERR_NETWORK' || err.message === 'Network Error') {
          code = 'NETWORK';
          message = 'Network error. Please check your internet connection.';
        } else if (err.response) {
          // Server responded with error
          code = `HTTP_${err.response.status}`;
          message = err.response.data?.detail || err.response.statusText || `Server error (${err.response.status})`;
        } else if (err.request) {
          // Request made but no response
          code = 'NO_RESPONSE';
          message = 'No response from server. The server may be down.';
        }
        
        setError(message);
        setErrorCode(code);
        
        // Retry up to 3 times with delay (only for network/timeout errors)
        if (retryCount < 3 && ['TIMEOUT', 'NETWORK', 'NO_RESPONSE'].includes(code)) {
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 2000);
        }
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, [retryCount]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  // Calculate read time (rough estimate: 200 words per minute)
  const getReadTime = (html) => {
    const text = html.replace(/<[^>]*>/g, '');
    const words = text.split(/\s+/).length;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  return (
    <div>
      {/* SEO for Blog List Page */}
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "Royal Security Blog",
            "description": "Expert advice on doors, windows, locks, and security",
            "url": "https://royalsecurity.info/blog",
            "publisher": {
              "@type": "Organization",
              "name": "Royal Security",
              "url": "https://royalsecurity.info"
            }
          })}
        </script>
      </Helmet>

      {/* Hero */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container">
          <div style={{
            display: 'inline-block',
            padding: '8px 16px',
            background: 'rgba(200, 168, 96, 0.2)',
            borderRadius: '6px',
            color: 'var(--royal-gold)',
            fontSize: '0.9rem',
            fontWeight: '600',
            marginBottom: '20px',
            border: '1px solid rgba(200, 168, 96, 0.3)'
          }}>
            Expert Insights
          </div>
          <h1 className="royal-heading-xl" style={{ color: 'var(--royal-white)', marginBottom: '16px' }}>
            Blog & Guides
          </h1>
          <p style={{ fontSize: '1.25rem', maxWidth: '700px', color: 'rgba(255, 255, 255, 0.9)' }}>
            Expert advice on doors, windows, locks, and security from Royal Security. 
            Tips, guides, and industry insights to help you make informed decisions.
          </p>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="royal-section" style={{ background: 'var(--bg-section-alt)' }}>
        <div className="royal-container">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              <div style={{
                width: '48px',
                height: '48px',
                border: '4px solid #e8e8e8',
                borderTopColor: 'var(--royal-gold)',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto'
              }} />
              <p style={{ marginTop: '16px', color: '#888' }}>Loading posts...</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : posts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '60px 0' }}>
              {error ? (
                <>
                  <h3 style={{ color: '#d32f2f', marginBottom: '12px' }}>Unable to Load Posts</h3>
                  <p style={{ color: '#666', marginBottom: '12px' }}>
                    {error}
                  </p>
                  {errorCode && (
                    <p style={{ 
                      color: '#999', 
                      fontSize: '0.8rem', 
                      marginBottom: '20px',
                      fontFamily: 'monospace',
                      background: '#f5f5f5',
                      padding: '8px 16px',
                      borderRadius: '4px',
                      display: 'inline-block'
                    }}>
                      Error Code: {errorCode} | Retry: {retryCount}/3
                    </p>
                  )}
                  <div style={{ marginTop: '16px' }}>
                    <button
                      onClick={() => setRetryCount(prev => prev + 1)}
                      style={{
                        padding: '12px 24px',
                        background: 'var(--royal-navy)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem',
                        marginRight: '12px'
                      }}
                    >
                      Try Again
                    </button>
                    <button
                      onClick={() => window.location.reload()}
                      style={{
                        padding: '12px 24px',
                        background: '#f5f5f5',
                        color: '#333',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontSize: '1rem'
                      }}
                    >
                      Refresh Page
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h3 style={{ color: 'var(--royal-navy)', marginBottom: '12px' }}>Coming Soon</h3>
                  <p style={{ color: '#666' }}>
                    We're working on bringing you valuable content about doors, windows, and security.
                    Check back soon!
                  </p>
                </>
              )}
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
              gap: '32px'
            }}>
              {posts.map((post, index) => (
                <article 
                  key={post.slug} 
                  style={{
                    background: 'white',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.12)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)';
                  }}
                >
                  {/* Thumbnail image - clickable */}
                  {post.thumbnail_url && (
                    <Link to={`/blog/${post.slug}`} style={{ display: 'block' }}>
                      <div style={{
                        width: '100%',
                        height: '200px',
                        overflow: 'hidden'
                      }}>
                        <img 
                          src={post.thumbnail_url} 
                          alt={post.title}
                          loading="lazy"
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            transition: 'transform 0.3s ease'
                          }}
                          onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                          onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                        />
                      </div>
                    </Link>
                  )}
                  
                  {/* Featured badge for first post */}
                  {index === 0 && !post.thumbnail_url && (
                    <div style={{
                      background: 'var(--royal-gold)',
                      color: 'var(--royal-navy)',
                      padding: '8px 16px',
                      fontSize: '0.8rem',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Latest Post
                    </div>
                  )}
                  
                  <div style={{ padding: '28px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    {/* Meta info */}
                    <div style={{
                      display: 'flex',
                      gap: '16px',
                      marginBottom: '16px',
                      fontSize: '0.85rem',
                      color: '#888'
                    }}>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={14} />
                        {formatDate(post.created_at)}
                      </span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Clock size={14} />
                        {getReadTime(post.html_content)}
                      </span>
                    </div>

                    {/* Title */}
                    <h2 style={{
                      fontSize: '1.35rem',
                      fontWeight: '700',
                      color: 'var(--royal-navy)',
                      marginBottom: '12px',
                      lineHeight: '1.3'
                    }}>
                      <Link 
                        to={`/blog/${post.slug}`}
                        style={{ color: 'inherit', textDecoration: 'none' }}
                        onMouseEnter={(e) => e.target.style.color = 'var(--royal-gold)'}
                        onMouseLeave={(e) => e.target.style.color = 'var(--royal-navy)'}
                      >
                        {post.title}
                      </Link>
                    </h2>

                    {/* Summary */}
                    <div 
                      style={{
                        color: '#666',
                        lineHeight: '1.7',
                        marginBottom: '20px',
                        flex: 1
                      }}
                      dangerouslySetInnerHTML={{ 
                        __html: post.summary || 'Click to read the full article...' 
                      }}
                    />

                    {/* Tags */}
                    {post.tags && post.tags.length > 0 && (
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        flexWrap: 'wrap',
                        marginBottom: '20px'
                      }}>
                        {post.tags.map((tag, i) => (
                          <span key={i} style={{
                            padding: '4px 10px',
                            background: '#f0f0f0',
                            borderRadius: '4px',
                            fontSize: '0.75rem',
                            color: '#666'
                          }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}

                    {/* Read More Link & Share Button */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                      <Link 
                        to={`/blog/${post.slug}`}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          color: 'var(--royal-gold)',
                          fontWeight: '600',
                          textDecoration: 'none',
                          fontSize: '0.95rem'
                        }}
                      >
                        Read Full Article →
                      </Link>
                      <ShareButtons 
                        url={`${window.location.origin}/blog/${post.slug}`}
                        title={post.title}
                        summary={post.summary}
                        imageUrl={post.thumbnail_url}
                        slug={post.slug}
                      />
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '60px 0',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ textAlign: 'center' }}>
          <h2 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            marginBottom: '16px',
            color: 'var(--royal-white)'
          }}>
            Need Expert Help?
          </h2>
          <p style={{
            fontSize: '1.1rem',
            marginBottom: '24px',
            color: 'rgba(255, 255, 255, 0.9)',
            maxWidth: '600px',
            margin: '0 auto 24px'
          }}>
            Get in touch for professional door, window, and lock services.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary" style={{ background: 'var(--royal-gold)', color: 'var(--royal-navy)' }}>
              Get a Quote
            </Link>
            <a href="tel:07710613783" className="royal-btn-light">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

// Single Blog Post Page
export const BlogPost = () => {
  useScrollAnimations();
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await api.get(`/blog/posts/${slug}`);
        setPost(response.data);
      } catch (error) {
        setError('Blog post not found');
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  useEffect(() => {
    if (loading) return;
    const canonical = canonicalFor(`/blog/${slug}`);
    if (!post) {
      applySeo({ title: 'Post Not Found | Royal Security Blog', description: 'The blog post you are looking for does not exist.', canonical, noindex: true });
      return;
    }
    const summary = post.summary ? post.summary.replace(/<[^>]*>/g, '').substring(0, 200) : post.title;
    applySeo({
      title: `${post.title} | Royal Security Blog`,
      description: summary,
      canonical,
      type: 'article',
      image: post.thumbnail_url || undefined,
      keywords: post.tags && post.tags.length > 0 ? post.tags.join(', ') : 'Royal Security Blog',
      article: { published: post.created_at, author: post.author || 'Royal Security', tags: post.tags }
    });
  }, [post, loading, slug]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-section-alt)'
      }}>
        <div style={{
          width: '48px',
          height: '48px',
          border: '4px solid #e8e8e8',
          borderTopColor: 'var(--royal-gold)',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error || !post) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        flexDirection: 'column',
        alignItems: 'center', 
        justifyContent: 'center',
        background: 'var(--bg-section-alt)',
        padding: '40px'
      }}>
        <h2 style={{ color: 'var(--royal-navy)', marginBottom: '16px' }}>Post Not Found</h2>
        <p style={{ color: '#666', marginBottom: '24px' }}>The blog post you're looking for doesn't exist.</p>
        <Link to="/blog" className="royal-btn-primary">
          ← Back to Blog
        </Link>
      </div>
    );
  }

  const pageUrl = `${window.location.origin}/blog/${post.slug}`;
  const plainSummary = post.summary ? post.summary.replace(/<[^>]*>/g, '').substring(0, 200) : post.title;

  return (
    <div>
      {/* SEO & Open Graph Meta Tags for Social Sharing */}
      <Helmet>
        {/* JSON-LD Structured Data for SEO - helps Google understand your content */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": post.title,
            "description": plainSummary,
            "image": post.thumbnail_url || "https://royalsecurity.info/favicon-192x192.png",
            "author": {
              "@type": "Organization",
              "name": "Royal Security",
              "url": "https://royalsecurity.info"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Royal Security",
              "logo": {
                "@type": "ImageObject",
                "url": "https://royalsecurity.info/favicon-192x192.png"
              }
            },
            "datePublished": post.created_at,
            "dateModified": post.updated_at || post.created_at,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://royalsecurity.info/blog/${post.slug}`
            },
            "keywords": post.tags ? post.tags.join(", ") : "locksmith, door repair, window repair"
          })}
        </script>
      </Helmet>

      {/* Header */}
      <section style={{
        background: 'linear-gradient(135deg, var(--royal-navy) 0%, #222222 100%)',
        padding: '80px 0 60px',
        color: 'var(--royal-white)'
      }}>
        <div className="royal-container" style={{ maxWidth: '900px' }}>
          <Link 
            to="/blog" 
            style={{ 
              color: 'var(--royal-gold)', 
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginBottom: '24px',
              fontSize: '0.95rem'
            }}
          >
            <ArrowLeft size={18} />
            Back to Blog
          </Link>
          
          <h1 style={{
            fontSize: 'clamp(1.75rem, 4vw, 2.5rem)',
            fontWeight: '700',
            lineHeight: '1.2',
            marginBottom: '20px',
            color: 'var(--royal-white)'
          }}>
            {post.title}
          </h1>
          
          <div style={{
            display: 'flex',
            gap: '24px',
            flexWrap: 'wrap',
            fontSize: '0.95rem',
            color: 'rgba(255,255,255,0.8)'
          }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <User size={16} />
              {post.author}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Calendar size={16} />
              {formatDate(post.created_at)}
            </span>
          </div>

          {post.tags && post.tags.length > 0 && (
            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap',
              marginTop: '20px'
            }}>
              {post.tags.map((tag, i) => (
                <span key={i} style={{
                  padding: '6px 12px',
                  background: 'rgba(200, 168, 96, 0.2)',
                  borderRadius: '4px',
                  fontSize: '0.8rem',
                  color: 'var(--royal-gold)'
                }}>
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Content */}
      <section style={{ padding: '60px 0', background: 'white' }}>
        <div className="royal-container" style={{ maxWidth: '800px' }}>
          {/* Thumbnail image if available */}
          {post.thumbnail_url && (
            <div style={{ marginBottom: '32px' }}>
              <img 
                src={post.thumbnail_url} 
                alt={post.title}
                style={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                  borderRadius: '12px'
                }}
              />
            </div>
          )}
          
          <article 
            className="blog-content"
            style={{
              fontSize: '1.1rem',
              lineHeight: '1.8',
              color: '#333'
            }}
            dangerouslySetInnerHTML={{ __html: post.html_content }}
          />
          
          {/* Blog content styles */}
          <style>{`
            .blog-content h1, .blog-content h2, .blog-content h3, .blog-content h4 {
              color: var(--royal-navy);
              margin-top: 2em;
              margin-bottom: 0.75em;
              font-weight: 700;
              line-height: 1.3;
            }
            .blog-content h1 { font-size: 2rem; }
            .blog-content h2 { font-size: 1.5rem; }
            .blog-content h3 { font-size: 1.25rem; }
            .blog-content p {
              margin-bottom: 1.5em;
            }
            .blog-content ul, .blog-content ol {
              margin-bottom: 1.5em;
              padding-left: 1.5em;
            }
            .blog-content li {
              margin-bottom: 0.5em;
            }
            .blog-content a {
              color: var(--royal-gold);
              text-decoration: underline;
            }
            .blog-content img {
              max-width: 100%;
              height: auto;
              border-radius: 8px;
              margin: 1.5em 0;
            }
            .blog-content blockquote {
              border-left: 4px solid var(--royal-gold);
              padding-left: 1.5em;
              margin: 1.5em 0;
              font-style: italic;
              color: #555;
            }
            .blog-content strong {
              font-weight: 600;
              color: var(--royal-navy);
            }
          `}</style>
          
          {/* Share Section */}
          <div style={{
            marginTop: '48px',
            paddingTop: '32px',
            borderTop: '1px solid #e8e8e8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '16px'
          }}>
            <div>
              <span style={{ fontWeight: '600', color: 'var(--royal-navy)', marginRight: '12px' }}>
                Share this article:
              </span>
              <ShareButtons 
                url={pageUrl}
                title={post.title}
                summary={plainSummary}
                imageUrl={post.thumbnail_url}
                slug={post.slug}
              />
            </div>
            <Link 
              to="/blog" 
              style={{ 
                color: 'var(--royal-gold)', 
                textDecoration: 'none',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              <ArrowLeft size={16} /> Back to Blog
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{
        background: 'var(--bg-section-alt)',
        padding: '60px 0',
        borderTop: '1px solid #e8e8e8'
      }}>
        <div className="royal-container" style={{ maxWidth: '800px', textAlign: 'center' }}>
          <h3 style={{ color: 'var(--royal-navy)', marginBottom: '16px', fontSize: '1.5rem' }}>
            Need Help With Your Doors or Windows?
          </h3>
          <p style={{ color: '#666', marginBottom: '24px' }}>
            Royal Security provides expert repairs and installations across East London and Essex.
          </p>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/contact" className="royal-btn-primary">
              Get a Quote
            </Link>
            <a href="tel:07710613783" className="royal-btn-secondary">
              Call 07710 613783
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
